import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import AIInsightsCard from '../components/AIInsightsCard';
import PredictionChart from '../components/PredictionChart';

const AIInsightsPage = () => {
  const [activeTab, setActiveTab] = useState('insights');
  const location = useLocation();
  
  useEffect(() => {
    // Check if tab parameter exists in URL
    const params = new URLSearchParams(location.search);
    const tabFromUrl = params.get('tab');
    
    // Only update if the tab is valid
    if (tabFromUrl && ['insights', 'recommendations', 'predictions'].includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [location]);
  
  // Mock user data for a teen user
  const userData = {
    screenTime: [
      { date: 'Mon', value: 3.2 },
      { date: 'Tue', value: 3.8 },
      { date: 'Wed', value: 4.5 },
      { date: 'Thu', value: 3.9 },
      { date: 'Fri', value: 5.2 },
      { date: 'Sat', value: 6.1 },
      { date: 'Sun', value: 5.8 },
    ],
    appUsage: [
      { app: 'TikTok', timeSpent: 92, icon: '📱', category: 'Social Media' },
      { app: 'YouTube', timeSpent: 78, icon: '🎬', category: 'Entertainment' },
      { app: 'Instagram', timeSpent: 65, icon: '📸', category: 'Social Media' },
      { app: 'Gaming', timeSpent: 120, icon: '🎮', category: 'Gaming' },
      { app: 'Educational', timeSpent: 30, icon: '📚', category: 'Learning' },
    ],
    habits: [
      { name: 'Mindful breaks', completed: 3, goal: 5 },
      { name: 'Focus sessions', completed: 4, goal: 6 },
      { name: 'Screen-free time', completed: 2, goal: 3 }
    ],
    weekdayUsage: 4.2,
    weekendUsage: 6.0,
    lateNightUsage: 72, // minutes after 10PM
    distractions: 18, // number of times distracted during study
    studyTime: 45, // minutes
    studyBreaks: 1, // number of breaks during study
    academicFocus: 2.5, // scale of 1-5
    sleepPattern: 'irregular'
  };
  
  // Mock insights
  const insights = [
    {
      id: 1,
      type: 'warning',
      title: 'Screen Time Trend',
      description: 'Your screen time has been increasing over the past few days.',
      category: 'screenTime',
      priority: 'high'
    },
    {
      id: 2,
      type: 'warning',
      title: 'Social Media Balance',
      description: 'Social media is taking up 45% of your screen time. Consider setting limits.',
      category: 'appUsage',
      priority: 'high'
    },
    {
      id: 3,
      type: 'suggestion',
      title: 'Learning Time',
      description: 'You\'re spending 3x more time on social apps than educational apps. Try to find more balance.',
      category: 'appUsage',
      priority: 'medium'
    },
    {
      id: 4,
      type: 'warning',
      title: 'Evening Screen Time',
      description: 'Using your phone after 8PM may be affecting your sleep quality and focus.',
      category: 'screenTime',
      priority: 'high'
    }
  ];
  
  // Mock recommendations
  const recommendations = [
    {
      id: 1,
      title: 'Screen Time Breaks',
      description: 'Take a 5-minute break for every 25 minutes of screen time to reduce eye strain and improve focus.',
      category: 'screenTime',
      priority: 'medium'
    },
    {
      id: 2,
      title: 'App Limits',
      description: 'Set daily time limits for social media apps to spend more time on other activities.',
      category: 'appUsage',
      priority: 'high'
    },
    {
      id: 3,
      title: 'Bedtime Mode',
      description: 'Enable "Sleep Guardian" mode to reduce blue light and notifications 1 hour before bed.',
      category: 'sleep',
      priority: 'high'
    },
    {
      id: 4,
      title: 'Focus Sessions',
      description: 'Try a 45-minute focused study session with your phone in another room to improve concentration.',
      category: 'focus',
      priority: 'medium'
    },
    {
      id: 5,
      title: 'Digital Sabbath',
      description: 'Designate one day a week as low-screen day to reconnect with offline activities.',
      category: 'balance',
      priority: 'low'
    }
  ];
  
  // Mock predictions
  const predictions = {
    predictions: [
      { day: 'Mon', hours: 4.2 },
      { day: 'Tue', hours: 4.3 },
      { day: 'Wed', hours: 4.5 },
      { day: 'Thu', hours: 4.6 },
      { day: 'Fri', hours: 4.8 }
    ],
    trend: 'increasing',
    message: 'Your screen time is trending upward. This could impact your homework time and sleep.',
    academicImpact: 'moderate',
    academicMessage: 'Your current screen time may have a moderate impact on your school performance.'
  };
  
  // Mock app usage analysis
  const appUsageAnalysis = {
    apps: userData.appUsage.slice(0, 5),
    categories: {
      'Social Media': 45,
      'Entertainment': 20,
      'Gaming': 25,
      'Learning': 10
    },
    totalUsage: 385,
    potentialIssues: [
      {
        app: 'TikTok',
        timeSpent: 92,
        message: 'High usage of TikTok detected. Consider setting time limits.'
      }
    ],
    distribution: [
      { name: 'TikTok', percentage: 24 },
      { name: 'YouTube', percentage: 20 },
      { name: 'Instagram', percentage: 17 },
      { name: 'Gaming', percentage: 31 },
      { name: 'Educational', percentage: 8 }
    ],
    insights: [
      {
        type: 'warning',
        title: 'Social Media Dominance',
        description: 'Social media is taking up nearly half of your screen time.'
      },
      {
        type: 'suggestion',
        title: 'Educational Content',
        description: 'Try adding more educational apps to your daily routine.'
      },
      {
        type: 'positive',
        title: 'Regular Breaks',
        description: 'You\'re taking breaks during long usage sessions, which is good for your eyes.'
      }
    ],
    peerComparison: {
      percentile: 65,
      yourDaily: 4.2,
      averageTeenDaily: 3.2
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">AI Insights for Teens</h1>
      <p className="text-gray-600 mb-8">
        Our AI analyzes your digital habits to help you find the right balance between screen time, school, and social life.
      </p>
      
      {/* Tab navigation */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="flex -mb-px space-x-8">
          <button
            onClick={() => setActiveTab('insights')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'insights'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Personalized Insights
          </button>
          <button
            onClick={() => setActiveTab('recommendations')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'recommendations'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Recommendations
          </button>
          <button
            onClick={() => setActiveTab('predictions')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'predictions'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Future Predictions
          </button>
        </nav>
      </div>
      
      {/* Insights Tab Content */}
      {activeTab === 'insights' && (
        <div>
          <div className="bg-indigo-50 rounded-xl p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Your Digital Wellbeing Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="text-xs uppercase text-gray-500 font-semibold">Daily Screen Time</div>
                <div className="mt-1 flex items-baseline">
                  <div className="text-2xl font-bold text-gray-900">{userData.screenTime[userData.screenTime.length - 1].value} hrs</div>
                  <div className="ml-2 text-sm text-gray-500">today</div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="text-xs uppercase text-gray-500 font-semibold">Weekly Average</div>
                <div className="mt-1 flex items-baseline">
                  <div className="text-2xl font-bold text-gray-900">
                    {(userData.screenTime.reduce((sum, day) => sum + day.value, 0) / userData.screenTime.length).toFixed(1)} hrs
                  </div>
                  <div className="ml-2 text-sm text-gray-500">per day</div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="text-xs uppercase text-gray-500 font-semibold">Most Used App</div>
                <div className="mt-1 flex items-center">
                  <span className="text-xl mr-2">{userData.appUsage[0].icon}</span>
                  <div>
                    <div className="text-lg font-bold text-gray-900">{userData.appUsage[0].app}</div>
                    <div className="text-xs text-gray-500">{userData.appUsage[0].timeSpent} min today</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="text-xs uppercase text-gray-500 font-semibold">Peer Comparison</div>
                <div className="mt-1 flex items-baseline">
                  <div className="text-2xl font-bold text-gray-900">
                    {appUsageAnalysis?.peerComparison?.percentile}%
                  </div>
                  <div className="ml-2 text-sm text-gray-500">
                    {appUsageAnalysis?.peerComparison?.yourDaily > appUsageAnalysis?.peerComparison?.averageTeenDaily ? 
                      'higher than avg teen' : 'lower than avg teen'}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <h2 className="text-xl font-bold text-gray-900 mb-4">AI-Generated Insights</h2>
          <p className="text-gray-600 mb-6">These insights are specifically tailored for teen digital habits and wellbeing.</p>
          
          {insights.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {insights.map((insight, index) => (
                <AIInsightsCard key={insight.id || index} insight={insight} />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className="text-gray-500">Keep using the app to get personalized insights!</p>
            </div>
          )}
          
          <div className="mt-8 bg-indigo-50 rounded-xl p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">App Usage Analysis</h3>
            {appUsageAnalysis && (
              <div>
                <div className="bg-white rounded-lg p-5 shadow-sm mb-6">
                  <h4 className="font-medium text-gray-900 mb-3">Distribution of Your Screen Time</h4>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {appUsageAnalysis.distribution.map((app, index) => (
                      <div key={index} className="text-center">
                        <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 text-indigo-600 text-xl mb-2">
                          {userData.appUsage[index]?.icon || '📱'}
                        </div>
                        <div className="text-sm font-medium text-gray-900">{app.name}</div>
                        <div className="text-xs text-gray-500">{app.percentage}%</div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-5 shadow-sm">
                  <h4 className="font-medium text-gray-900 mb-3">What This Means For You</h4>
                  <div className="space-y-4">
                    {appUsageAnalysis.insights.map((insight, index) => (
                      <div key={index} className="flex items-start">
                        <div className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center text-white
                          ${insight.type === 'warning' ? 'bg-red-500' : 
                            insight.type === 'positive' ? 'bg-green-500' : 
                            insight.type === 'suggestion' ? 'bg-blue-500' : 'bg-purple-500'}`}>
                          {insight.type === 'warning' ? '!' : 
                           insight.type === 'positive' ? '✓' : 
                           insight.type === 'suggestion' ? '?' : 'i'}
                        </div>
                        <div className="ml-4">
                          <h5 className="text-base font-medium text-gray-900">{insight.title}</h5>
                          <p className="mt-1 text-sm text-gray-600">{insight.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Recommendations Tab Content */}
      {activeTab === 'recommendations' && (
        <div>
          <div className="bg-indigo-50 rounded-xl p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900">Teen-Specific Recommendations</h2>
            <p className="text-gray-600 mt-2 mb-0">
              These recommendations are created specifically for teen brains and habits, based on the latest research and your personal usage patterns.
            </p>
          </div>
          
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mb-8">
            {recommendations.map((recommendation) => (
              <div 
                key={recommendation.id} 
                className={`bg-white rounded-xl shadow overflow-hidden border-t-4 
                  ${recommendation.priority === 'high' ? 'border-red-500' : 
                    recommendation.priority === 'medium' ? 'border-yellow-500' : 'border-green-500'}`}
              >
                <div className="p-5">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-lg font-bold text-gray-900">{recommendation.title}</h3>
                    <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                      recommendation.priority === 'high' ? 'bg-red-100 text-red-800' : 
                      recommendation.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' : 
                      'bg-green-100 text-green-800'
                    }`}>
                      {recommendation.priority.charAt(0).toUpperCase() + recommendation.priority.slice(1)}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-4">{recommendation.description}</p>
                  <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
                    Category: {recommendation.category.charAt(0).toUpperCase() + recommendation.category.slice(1)}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl overflow-hidden shadow-lg">
            <div className="p-6 sm:p-10">
              <h3 className="text-xl font-bold text-white mb-2">Want Personalized Recommendations?</h3>
              <p className="text-indigo-100 mb-6">
                The more you use the app, the more tailored our recommendations will become to your specific habits and goals.
              </p>
              <Link 
                to="/habits" 
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-indigo-600 bg-white hover:bg-indigo-50"
              >
                Set Your Digital Goals
              </Link>
            </div>
          </div>
        </div>
      )}
      
      {/* Predictions Tab Content */}
      {activeTab === 'predictions' && (
        <div>
          <div className="bg-indigo-50 rounded-xl p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900">Screen Time Predictions</h2>
            <p className="text-gray-600 mt-2 mb-0">
              Our AI analyzes your patterns to predict future usage and how it might affect your academic performance, sleep, and wellbeing.
            </p>
          </div>
          
          {predictions ? (
            <div className="mb-8">
              <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Your Next 5 Days</h3>
                <div className="h-64 mb-4">
                  <PredictionChart 
                    historicalData={userData.screenTime}
                    predictions={predictions.predictions} 
                  />
                </div>
                <div className={`p-4 rounded-lg mt-4 text-sm
                  ${predictions.trend === 'increasing' ? 'bg-red-50 text-red-800' : 
                   predictions.trend === 'decreasing' ? 'bg-green-50 text-green-800' : 
                   'bg-blue-50 text-blue-800'}`}>
                  <p className="font-medium">
                    {predictions.message}
                  </p>
                </div>
              </div>
              
              <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Potential Impact on School</h3>
                <div className={`p-4 rounded-lg text-sm mb-4
                  ${predictions.academicImpact === 'high' ? 'bg-red-50 text-red-800 border-l-4 border-red-500' : 
                   predictions.academicImpact === 'moderate' ? 'bg-yellow-50 text-yellow-800 border-l-4 border-yellow-500' : 
                   'bg-green-50 text-green-800 border-l-4 border-green-500'}`}>
                  <div className="font-medium mb-1">
                    {predictions.academicImpact.charAt(0).toUpperCase() + predictions.academicImpact.slice(1)} Impact on School
                  </div>
                  <p>
                    {predictions.academicMessage}
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm font-medium text-gray-900 mb-1">Focus & Attention</div>
                    <div className="text-xs text-gray-600">
                      {predictions.academicImpact === 'high' ? 
                        'May struggle to focus during classes and homework time' : 
                        predictions.academicImpact === 'moderate' ? 
                        'Some impact on class and homework attention' : 
                        'Good focus levels expected'}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm font-medium text-gray-900 mb-1">Homework Quality</div>
                    <div className="text-xs text-gray-600">
                      {predictions.academicImpact === 'high' ? 
                        'Higher chance of rushing through assignments' : 
                        predictions.academicImpact === 'moderate' ? 
                        'May need more time to complete quality work' : 
                        'Should be able to produce quality work efficiently'}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm font-medium text-gray-900 mb-1">Memory Retention</div>
                    <div className="text-xs text-gray-600">
                      {predictions.academicImpact === 'high' ? 
                        'May have difficulty remembering lessons and study material' : 
                        predictions.academicImpact === 'moderate' ? 
                        'Some impact on memory and information processing' : 
                        'Good memory consolidation expected'}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Steps You Can Take</h3>
                <div className="space-y-4">
                  {predictions.trend === 'increasing' && (
                    <>
                      <div className="flex items-start">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="ml-4">
                          <h5 className="text-base font-medium text-gray-900">Set Daily Limits</h5>
                          <p className="mt-1 text-sm text-gray-600">
                            Create app-specific time limits for your most used social apps. Even a 20-minute reduction can make a difference.
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                        </div>
                        <div className="ml-4">
                          <h5 className="text-base font-medium text-gray-900">Try Focus Mode During Homework</h5>
                          <p className="mt-1 text-sm text-gray-600">
                            Use our Focus Mode to block distracting apps during homework time.
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h5 className="text-base font-medium text-gray-900">Protect Your Sleep</h5>
                      <p className="mt-1 text-sm text-gray-600">
                        Activate Sleep Guardian to reduce blue light and notifications after 9PM. Teen brains need 8-10 hours of quality sleep.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h5 className="text-base font-medium text-gray-900">Balance School Days vs. Weekends</h5>
                      <p className="mt-1 text-sm text-gray-600">
                        Keep weekday screen time under 4 hours for better academic results. Save more media time for weekends.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <p className="text-gray-500 mb-2">Not enough data to generate predictions yet.</p>
              <p className="text-gray-500">Continue using the app to get personalized predictions.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AIInsightsPage; 